import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn } from 'components/create-dialog/useCreateDraft';
import Loader from 'components/Loader';
import { useMemo, useState } from 'react';
import { useGetPartyGroupByIdQuery } from 'store/api/groups';
import { GroupPartnershipType, GroupType, PartyGroupForm } from 'types/groups';
import { SegmentType } from 'types/segment';
import { ServiceLevelType } from 'types/service-level';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { GROUP_FORM_CLASSIFIERS, groupDtoToForm } from './groupFormMapping';
import GroupPageForm from './GroupPageForm';

/** The add form loaded with what a draft holds, read as the group's edit page reads it. */
const GroupDraftAdd = ({ draftId }: { draftId: number }) => {
    const [initialData, setInitialData] = useState<PartyGroupForm>();
    const { data: partyGroup } = useGetPartyGroupByIdQuery(String(draftId), { refetchOnMountOrArgChange: true });
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: GROUP_FORM_CLASSIFIERS });

    // Frozen once loaded: a refetch must not reset what the user is typing.
    if (!initialData && partyGroup && !isLoadingClassifiers) {
        setInitialData(groupDtoToForm(partyGroup, classifiers));
    }

    return initialData ? <GroupPageForm type="add" initialData={initialData} classifiers={classifiers} /> : <Loader />;
};

const GroupBlankAdd = (): JSX.Element => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['GROUP_PARTNERSHIP_TYPE', 'BUSINESS_AREA_TYPE', 'SEGMENT_TYPE', 'SERVICE_LEVEL_TYPE'],
    });

    // Derive initial form data from classifiers during render (no effect needed).
    const initialData = useMemo<PartyGroupForm | undefined>(() => {
        if (!classifiers) return undefined;

        const defaultServiceLevelType =
            getOptionsFromClassifier<string, string>(classifiers['SERVICE_LEVEL_TYPE']).find(
                (val) => val.value === ServiceLevelType.EXPLORE
            ) ?? null;

        const defaultSegmentType =
            getOptionsFromClassifier<string, string>(classifiers['SEGMENT_TYPE']).find(
                (val) => val.value === SegmentType.NEW
            ) ?? null;

        return {
            groupType: GroupType.CORPORATE,
            groupName: '',
            businessAreas: [],
            members: [],
            primaryContacts: [],
            relationshipManagers: [],
            relationshipPartners: [],
            groupPartnershipType:
                getOptionsFromClassifier<string, string>(classifiers['GROUP_PARTNERSHIP_TYPE']).find(
                    (val) => val.value === GroupPartnershipType.REGULAR
                ) ?? null,
            segmentType: defaultSegmentType,
            serviceLevelType: defaultServiceLevelType,
            notes: '',
        };
    }, [classifiers]);

    if (isLoadingClassifiers || initialData === undefined) {
        return <Loader />;
    }

    return <GroupPageForm type="add" classifiers={classifiers} initialData={initialData} />;
};

/** The group create; its URL with `?draft=<id>` edits that draft instead of a blank group. */
const GroupPageAdd = (): JSX.Element => {
    const draftId = draftIdIn(useCreateRoute().location.search);
    return draftId === null ? <GroupBlankAdd /> : <GroupDraftAdd key={draftId} draftId={draftId} />;
};

export default GroupPageAdd;
